/**
 * use-onboarding-return.ts — trang đích mở từ trang "Bắt đầu" (`?from=onboarding&step=<n>`).
 *
 * Khi thao tác thành công (kết nối kênh, lưu nhân viên AI, tạo quy trình, mời đồng nghiệp), trang gọi
 * `complete(step)`: toast "Đã xong bước N — Quay lại trang Bắt đầu" (nút quay lại + "Ở lại trang này"),
 * và tự chuyển về `/onboarding#step-<kế tiếp>` sau ~2 giây nếu người dùng không chọn ở lại.
 * Không có `from=onboarding` trên URL → không làm gì (luồng thường không bị ảnh hưởng). URL ghi bước khác
 * (vd đang ở bước 5 mà tạo nhân viên AI) → cũng bỏ qua, tránh báo nhầm bước.
 *
 * Thao tác xong mà mở tiếp một trình soạn (tạo nhân viên AI → màn Train, tạo quy trình → trình dựng luồng):
 * gọi `complete(step, { autoReturn: false })` + điều hướng bằng `carry(to)` để giữ `from/step` — không tự kéo
 * người dùng đi giữa lúc soạn, nhưng toast và dải "Quay lại trang Bắt đầu" vẫn còn.
 */
import { createElement, useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n } from '@/lib/i18n'
import { onboardingKeys } from '@/hooks/use-onboarding'
import { OnboardingDoneToast } from '@/components/shared/onboarding-done-toast'
import {
  nextPendingStep,
  onboardingReturnPath,
  readOnboardingOrigin,
  stepTitle,
  stripOnboardingOrigin,
  withOnboardingOrigin,
  type OnboardingStatus,
  type OnboardingStepNo,
} from '@/lib/onboarding'

export const ONBOARDING_AUTO_RETURN_MS = 2000

/** Hẹn giờ tự quay về đang chờ — một cái cho cả ứng dụng (bước mới thay bước cũ). */
let pending: { timer: ReturnType<typeof setTimeout>; toastId: string | number } | null = null

function cancelPending() {
  if (!pending) return
  clearTimeout(pending.timer)
  pending = null
}

export function useOnboardingReturn() {
  const [sp] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const origin = readOnboardingOrigin(sp)
  const active = !!origin
  const originStep = origin?.step ?? null

  const complete = useCallback(
    (step: OnboardingStepNo, opts: { delayMs?: number; autoReturn?: boolean } = {}) => {
      if (!active || (originStep !== null && originStep !== step)) return
      void qc.invalidateQueries({ queryKey: onboardingKeys.status })
      const status = qc.getQueryData<OnboardingStatus>(onboardingKeys.status)
      const target = onboardingReturnPath(nextPendingStep(status, step))
      const startPath = location.pathname
      const delay = opts.delayMs ?? ONBOARDING_AUTO_RETURN_MS
      const autoReturn = opts.autoReturn !== false

      cancelPending()
      const title = i18n.t('sharedMisc:onboardingReturn.stepDoneTitle', { step, stepTitle: stepTitle(step) })
      if (!autoReturn) {
        toast.custom(
          (id) =>
            createElement(OnboardingDoneToast, {
              title,
              onBack: () => {
                toast.dismiss(id)
                navigate(target)
              },
            }),
          { duration: 8000 },
        )
        return
      }
      const toastId = toast.custom(
        (id) =>
          createElement(OnboardingDoneToast, {
            title,
            note: i18n.t('sharedMisc:onboardingReturn.autoReturnNote', { seconds: Math.round(delay / 1000) }),
            onBack: () => {
              cancelPending()
              toast.dismiss(id)
              navigate(target) // push: Back quay lại đúng trang vừa làm
            },
            onStay: () => {
              cancelPending()
              toast.dismiss(id)
              // Bỏ from/step để dải "Quay lại" và việc tự chuyển không bật lại trên trang này.
              navigate(
                { pathname: location.pathname, search: stripOnboardingOrigin(location.search), hash: location.hash },
                { replace: true },
              )
            },
          }),
        { duration: delay + 3000 },
      )
      const timer = setTimeout(() => {
        pending = null
        // Người dùng đã tự đi trang khác trong lúc chờ → không kéo họ đi.
        if (window.location.pathname !== startPath) return
        toast.dismiss(toastId)
        navigate(target)
      }, delay)
      pending = { timer, toastId }
    },
    [active, originStep, qc, navigate, location.pathname, location.search, location.hash],
  )

  /** Đường tới trang kế (trình soạn) giữ nguyên `from=onboarding&step` khi đang trong luồng Bắt đầu. */
  const carry = useCallback(
    (to: string, step: OnboardingStepNo) => (active ? withOnboardingOrigin(to, originStep ?? step) : to),
    [active, originStep],
  )

  return { active, step: originStep, complete, carry }
}
